/**
 * A 401 on a signed-in call — the session is gone. Wired from HttpClient and
 * answered by each console's root, which goes straight back to its sign-in
 * form instead of leaving every screen to print "Your session has ended".
 *
 * Kept React-free so client.ts can import it, and a window event rather than
 * a module-level listener list for the reason `isSessionExpiredError` gives:
 * Vite can load two copies of a workspace module, and a listener registered
 * on one copy would never hear the other.
 */

export const SESSION_EXPIRED_EVENT = 'affdice:session-expired';

export function notifySessionExpired(path: string): void {
  const target = typeof globalThis.window !== 'undefined' ? globalThis.window : globalThis;
  if (typeof target.dispatchEvent !== 'function') return;
  target.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { path } }));
}

/**
 * The boot probe's 401 is its answer — "this cookie alone is not a session" —
 * not a session ending. Signalled, it would sign out a tab that "Login as"
 * opened with a token of its own, whose cookie-only probe is expected to find
 * nothing. Logout is on its way to the sign-in form already.
 */
const NOT_A_SESSION_ENDING: readonly RegExp[] = [/^\/v1\/session$/, /^\/v1\/logout$/];

export function shouldSignalSessionExpired(path: string): boolean {
  const bare = path.split('?')[0] ?? path;
  return !NOT_A_SESSION_ENDING.some((re) => re.test(bare));
}

export function maybeNotifySessionExpired(path: string): void {
  if (shouldSignalSessionExpired(path)) notifySessionExpired(path);
}
